import { assert } from 'site/sdk/assert.ts'
import { throttle } from 'site/sdk/throttle.ts'
import { useEffect } from 'preact/hooks'
import { deadline } from 'std/async/deadline.ts'
import { chunk } from 'std/collections/chunk.ts'

export interface Props {
    rootId: string
    scroll?: 'smooth' | 'auto'
    interval?: number
    infinite?: boolean
    dotIsPage?: boolean
    startFrom?: number
    buttonsId?: string
}

const ATTRIBUTES = {
    'data-slider': 'data-slider',
    'data-slider-item': 'data-slider-item',
    'data-slide="prev"': 'data-slide="prev"',
    'data-slide="next"': 'data-slide="next"',
    'data-slide-dot="prev"': 'data-slide-dot="prev"',
    'data-slide-dot="next"': 'data-slide-dot="next"',
    'data-dot': 'data-dot',
    'data-dot-group': 'data-dot-group',
}

// Percentage of the item that has to be inside the container
// for it it be considered as inside the container
const THRESHOLD = 0.6

const intersectionX = (element: DOMRect, container: DOMRect): number => {
    const delta = container.width / 1_000

    if (element.right < container.left - delta) {
        return 0.0
    }

    if (element.left > container.right + delta) {
        return 0.0
    }

    if (element.left < container.left - delta) {
        return element.right - container.left + delta
    }

    if (element.right > container.right + delta) {
        return container.right - element.left + delta
    }

    return element.width
}

const waitForElement = (selector: string): Promise<void> => {
    return new Promise((resolve) => {
        const observer = new MutationObserver((mutations, observer) => {
            if (document.querySelector(selector)) {
                observer.disconnect()
                resolve()
            }
        })

        observer.observe(document.body, { childList: true, subtree: true })
    })
}

const setup = async ({
    rootId,
    scroll,
    interval,
    infinite,
    dotIsPage = false,
    startFrom = 0,
    buttonsId,
}: Props) => {
    try {
        await deadline(waitForElement(`#${rootId}`), 3000)
    } catch {
        if (!document.getElementById(rootId)) {
            throw new Error(`Unable to find root element with id ${rootId}`)
        }
    }

    const root = document.getElementById(rootId) as HTMLElement

    const slider = root.querySelector(`[${ATTRIBUTES['data-slider']}]`)
    const prevs = root.querySelectorAll<HTMLElement>(
        `${buttonsId ? `#${buttonsId}` : ''}[${ATTRIBUTES['data-slide="prev"']}]`,
    )
    const nexts = root.querySelectorAll<HTMLElement>(
        `${buttonsId ? `#${buttonsId}` : ''}[${ATTRIBUTES['data-slide="next"']}]`,
    )
    let dotsGroups = root.querySelectorAll(`[${ATTRIBUTES['data-dot-group']}]`)
    const items = root.querySelectorAll<HTMLElement>(`[${ATTRIBUTES['data-slider-item']}]`)

    if (!slider) throw new Error('Unable to find slider element')
    if (!items || items.length === 0) console.warn('Unable to find items element')

    // if there is dotsGroups get dots for each group, else get dots for root
    let dots = [...(dotsGroups?.length ? dotsGroups.values() : [root])].map((e) =>
        e.querySelectorAll(`[${ATTRIBUTES['data-dot']}]`)
    )

    let currentIndex = startFrom

    const getElementsInsideContainer = () => {
        const indices: number[] = []
        const sliderRect = slider.getBoundingClientRect()

        for (let index = 0; index < items.length; index++) {
            const item = items.item(index)
            const rect = item.getBoundingClientRect()

            const ratio = intersectionX(rect, sliderRect) / rect.width

            if (ratio > THRESHOLD) {
                indices.push(index)
            }
        }

        return indices
    }

    const goToItem = (index: number) => {
        slider.scrollTo({
            top: 0,
            behavior: scroll,
            left: items[index].offsetLeft,
        })
    }

    const _onClickPrev = () => {
        const indices = getElementsInsideContainer()
        const itemsPerPage = indices.length
        const isShowingFirst = indices[0] === 0
        const rangeItems = dotIsPage ? chunkedElements.length : items.length

        if (infinite) {
            currentIndex -= 1

            if (currentIndex < 0) {
                currentIndex = rangeItems - 1
            }
        } else {
            currentIndex = Math.max(0, currentIndex - 1)
        }

        goToItem(isShowingFirst ? items.length - 1 : Math.max(0, indices[0] - itemsPerPage))
    }

    const _onClickNext = () => {
        const indices = getElementsInsideContainer()
        const isShowingLast = indices[indices.length - 1] === items.length - 1
        const rangeItems = dotIsPage ? chunkedElements.length : items.length

        if (infinite) {
            currentIndex += 1

            if (currentIndex >= rangeItems) {
                currentIndex = 0
            }
        } else {
            currentIndex = Math.min(rangeItems - 1, currentIndex + 1)
        }

        goToItem(isShowingLast ? 0 : Math.min(indices[0] + indices.length, items.length - 1))
    }

    const [onClickNext] = throttle(_onClickNext, 500)
    const [onClickPrev] = throttle(_onClickPrev, 500)

    let chunkedElements = [] as number[]

    if (dotIsPage) {
        const indexes = [...items.entries()].map((_, i) => i)
        const insideElements = getElementsInsideContainer()
        if (insideElements.length === 0) return

        chunkedElements = chunk(indexes, insideElements.length).map((i) => i[0])

        for (const _dots of dots) {
            for (const [i, e] of _dots.entries()) {
                if (!chunkedElements.includes(i)) {
                    assert.unreachable(e.parentElement).remove()
                }
            }
        }

        dotsGroups = root.querySelectorAll(`[${ATTRIBUTES['data-dot-group']}]`)

        // if there is dotsGroups get dots for each group, else get dots for root
        dots = [...(dotsGroups?.length ? dotsGroups.values() : [root])].map((e) =>
            e.querySelectorAll(`[${ATTRIBUTES['data-dot']}]`)
        )
    }

    const observer = new IntersectionObserver(
        (elements) => {
            for (const item of elements) {
                const index = Number(item.target.getAttribute('data-slider-item')) || 0

                if (item.isIntersecting) {
                    item.target.setAttribute('data-intersection', '1')
                } else {
                    item.target.removeAttribute('data-intersection')
                }

                if (!infinite) {
                    if (index === 0) {
                        if (item.isIntersecting) {
                            for (const prev of prevs) {
                                prev.setAttribute('disabled', '')
                                prev.classList.add('opacity-60', 'transition-opacity')
                            }
                        } else {
                            for (const prev of prevs) {
                                prev.removeAttribute('disabled')
                                prev.classList.remove('opacity-60', 'transition-opacity')
                            }
                        }
                    }
                    if (index === items.length - 1) {
                        if (item.isIntersecting) {
                            for (const next of nexts) {
                                next.setAttribute('disabled', '')
                                next.classList.add('opacity-60', 'transition-opacity')
                            }
                        } else {
                            for (const next of nexts) {
                                next.removeAttribute('disabled')
                                next.classList.remove('opacity-60', 'transition-opacity')
                            }
                        }
                    }
                }

                const inside = getElementsInsideContainer()

                const chunked = chunk(
                    Array(items.length)
                        .fill(0)
                        .map((_, i) => i),
                    inside.length,
                )

                if (dotIsPage) {
                    const dotIndex = chunked.findIndex(
                        (i) => i.includes(index) && item.isIntersecting,
                    )

                    if (dotIndex > -1) {
                        currentIndex = dotIndex
                    }
                } else if (item.isIntersecting && !infinite) {
                    currentIndex = index
                } else if (item.isIntersecting && infinite) {
                    currentIndex = index
                }
            }

            for (const dot of dots) {
                for (const [i, e] of dot.entries()) {
                    if (i === currentIndex) {
                        e.setAttribute('data-active', '1')
                    } else {
                        e.removeAttribute('data-active')
                    }
                }
            }
        },
        { threshold: dotIsPage ? 0.1 : THRESHOLD, root: slider },
    )

    for (const item of items) observer.observe(item)
    if (startFrom) goToItem(startFrom)

    const inside = getElementsInsideContainer()
    if (inside.length > 0) {
        root.setAttribute('data-has-invisible', String(inside.length !== items.length))
    }

    for (const _dots of dots) {
        for (let it = 0; it < (_dots?.length ?? 0); it++) {
            _dots?.item(it).addEventListener('click', () => {
                currentIndex = it
                goToItem(dotIsPage ? chunkedElements[it] : it)
            })
        }
    }

    for (const prev of prevs) prev.addEventListener('click', onClickPrev)
    for (const next of nexts) next.addEventListener('click', onClickNext)

    const timeout = interval && setInterval(onClickNext, interval)

    // Unregister callbacks
    return () => {
        for (const _dots of dots) {
            for (let it = 0; it < (_dots?.length ?? 0); it++) {
                _dots?.item(it).removeEventListener('click', () => {
                    currentIndex = it
                    goToItem(dotIsPage ? chunkedElements[it] : it)
                })
            }
        }

        for (const prev of prevs) prev.removeEventListener('click', onClickPrev)
        for (const next of nexts) next.removeEventListener('click', onClickNext)

        observer.disconnect()

        clearInterval(timeout)
    }
}

function Slider({
    rootId,
    scroll = 'smooth',
    interval,
    infinite = false,
    dotIsPage,
    startFrom,
    buttonsId,
}: Props) {
    useEffect(() => {
        setup({ rootId, scroll, interval, infinite, dotIsPage, startFrom, buttonsId })
    }, [])

    return <div data-slider-controller-js />
}

export default Slider
